import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Link
} from '@mui/material';
import {
    Close as CloseIcon,
    CloudUpload as UploadIcon,
    VideoFile as VideoIcon,
    Image as ImageIcon,
    Add as AddIcon
} from '@mui/icons-material';
import tutorialApi from 'src/utils/tutorialApi';
import { showErrorToast, showSuccessToast } from 'src/utils/Toast/SnackbarProvider';

const difficultyOptions = [
    { value: 'beginner', label: 'Beginner (Easy)' },
    { value: 'intermediate', label: 'Intermediate (Medium)' },
    { value: 'advanced', label: 'Advanced (Hard)' }
];

export default function UploadVideoDialog({ open, onClose, categoriesData, onUploadSuccess, editingTutorial }) {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category_id: '',
        difficulty: 'beginner',
        duration: '',
        tags: '',
        order: 0
    });
    const [videoFile, setVideoFile] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [categoryList, setCategoryList] = useState([]);
    
    // New Category State
    const [isCreatingCategory, setIsCreatingCategory] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');

    // Update category list when categoriesData changes
    React.useEffect(() => {
        if (categoriesData && typeof categoriesData === 'object') {
            // Convert categories object to array
            const catArray = Object.entries(categoriesData)
                .filter(([name]) => name !== 'All')
                .map(([name, data]) => ({
                    name,
                    ...data
                }));
            setCategoryList(catArray);
        }
    }, [categoriesData]);

    // Populate form when editing
    React.useEffect(() => {
        if (editingTutorial) {
            // Get category_id from either category_id field or category.id
            const categoryId = editingTutorial.category_id || editingTutorial.category?.id || '';
            
            setFormData({
                title: editingTutorial.title || '',
                description: editingTutorial.description || '',
                category_id: categoryId,
                difficulty: editingTutorial.difficulty || 'beginner',
                duration: editingTutorial.duration || '',
                tags: Array.isArray(editingTutorial.tags) ? editingTutorial.tags.join(', ') : '',
                order: editingTutorial.order || 0
            });
        } else {
            // Reset form when not editing
            setFormData({
                title: '',
                description: '',
                category_id: '',
                difficulty: 'beginner',
                duration: '',
                tags: '',
                order: 0
            });
            setVideoFile(null);
            setThumbnailFile(null);
        }
    }, [editingTutorial]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleVideoSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file size (500MB)
            if (file.size > 524288000) {
                showErrorToast('Video file size cannot exceed 500MB');
                return;
            }
            // Validate file type
            const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
            if (!allowedTypes.includes(file.type)) {
                showErrorToast('Please upload a valid video file (mp4, webm, mov, avi)');
                return;
            }
            setVideoFile(file);
        }
    };

    const handleThumbnailSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file size (10MB)
            if (file.size > 10485760) {
                showErrorToast('Thumbnail image size cannot exceed 10MB');
                return;
            }
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                showErrorToast('Please upload a valid image file (jpg, png, webp)');
                return;
            }
            setThumbnailFile(file);
        }
    };

    const handleSubmit = async () => {
        // Validation
        if (!formData.title.trim()) {
            showErrorToast('Please enter a title');
            return;
        }

        let finalCategoryId = formData.category_id;

        // Create new category if needed
        if (isCreatingCategory) {
            if (!newCategoryName.trim()) {
                showErrorToast('Please enter a category name');
                return;
            }
            setIsUploading(true);
            try {
                // Generate a label from the name (capitalize first letter)
                const label = newCategoryName.charAt(0).toUpperCase() + newCategoryName.slice(1);
                
                // You might want to let user pick color/icon, but for now use defaults
                const catResponse = await tutorialApi.createCategory({
                    master_role_id: 1, // Default to admin for now? Or make it optional in backend
                    name: newCategoryName,
                    label: label,
                    activated: true,
                    color: '#00AB55', // Default green
                    icon: 'header',  // Default icon
                    order: 99
                });
                
                if (catResponse.data?.success || catResponse.data?.id) {
                     finalCategoryId = catResponse.data.data?.id || catResponse.data.id;
                } else {
                    showErrorToast('Failed to create new category');
                    setIsUploading(false);
                    return;
                }
            } catch (error) {
                console.error('Error creating category:', error);
                showErrorToast('Failed to create category');
                setIsUploading(false);
                return;
            }
        } else {
            if (!formData.category_id) {
                showErrorToast('Please select a category');
                return;
            }
        }

        if (!videoFile && !editingTutorial) {
            showErrorToast('Please select a video file');
            return;
        }

        setIsUploading(true);

        try {
            // Prepare form data for multipart upload
            const uploadData = new FormData();
            uploadData.append('title', formData.title);
            uploadData.append('description', formData.description);
            uploadData.append('category_id', finalCategoryId);
            uploadData.append('difficulty', formData.difficulty);
            uploadData.append('duration', formData.duration || '0:00');
            uploadData.append('order', formData.order);
            
            // Parse tags
            const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
            uploadData.append('tags', JSON.stringify(tagsArray));

            // Append files (only if provided)
            if (videoFile) {
                uploadData.append('video_file', videoFile);
            }
            if (thumbnailFile) {
                uploadData.append('thumbnail', thumbnailFile);
            }

            // Upload or update tutorial
            const response = editingTutorial 
                ? await tutorialApi.updateTutorial(editingTutorial.id, uploadData)
                : await tutorialApi.createTutorial(uploadData);

            if (response.data?.success) {
                showSuccessToast(editingTutorial ? 'Tutorial updated successfully!' : 'Tutorial uploaded successfully!');
                handleClose();
                if (onUploadSuccess) {
                    onUploadSuccess();
                }
            } else {
                showErrorToast(editingTutorial ? 'Failed to update tutorial' : 'Failed to upload tutorial');
            }
        } catch (error) {
            console.error('Upload error:', error);
            const errorMessage = error.response?.data?.message || 'Failed to upload tutorial';
            showErrorToast(errorMessage);
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            title: '',
            description: '',
            category_id: '',
            difficulty: 'beginner',
            duration: '',
            tags: '',
            order: 0
        });
        setNewCategoryName('');
        setIsCreatingCategory(false);
        setVideoFile(null);
        setThumbnailFile(null);
        setIsUploading(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{editingTutorial ? 'Edit Tutorial Video' : 'Upload Tutorial Video'}</Typography>
                    <IconButton onClick={handleClose} disabled={isUploading}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {/* Title */}
                    <TextField
                        label="Tutorial Title *"
                        fullWidth
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        disabled={isUploading}
                    />

                    {/* Category Selection or Creation */}
                    <Box>
                        {isCreatingCategory ? (
                            <TextField
                                label="New Category Name *"
                                fullWidth
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                disabled={isUploading}
                                helperText="This will create a new category section"
                            />
                        ) : (
                            <FormControl fullWidth required>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    value={formData.category_id}
                                    onChange={(e) => handleInputChange('category_id', e.target.value)}
                                    label="Category"
                                    disabled={isUploading}
                                >
                                    {categoryList.map((cat) => (
                                        <MenuItem key={cat.id} value={cat.id}>
                                            {cat.icon} {cat.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                            <Link 
                                component="button"
                                variant="body2"
                                onClick={() => {
                                    setIsCreatingCategory(!isCreatingCategory);
                                    if (!isCreatingCategory) {
                                        setFormData(prev => ({ ...prev, category_id: '' }));
                                    } else {
                                        setNewCategoryName('');
                                    }
                                }}
                                disabled={isUploading}
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                            >
                                {isCreatingCategory ? 'Select Existing Category' : 'Create New Category'}
                            </Link>
                        </Box>
                    </Box>

                    {/* Difficulty */}
                    <FormControl fullWidth>
                        <InputLabel>Difficulty</InputLabel>
                        <Select
                            value={formData.difficulty}
                            onChange={(e) => handleInputChange('difficulty', e.target.value)}
                            label="Difficulty"
                            disabled={isUploading}
                        >
                            {difficultyOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Description */}
                    <TextField
                        label="Description"
                        multiline
                        rows={3}
                        fullWidth
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        disabled={isUploading}
                    />

                    {/* Duration */}
                    <TextField
                        label="Duration (MM:SS)"
                        placeholder="5:30"
                        fullWidth
                        value={formData.duration}
                        onChange={(e) => handleInputChange('duration', e.target.value)}
                        disabled={isUploading}
                        helperText="Optional - Format: MM:SS (e.g., 5:30)"
                    />

                    {/* Tags */}
                    <TextField
                        label="Tags (comma-separated)"
                        placeholder="onboarding, setup, guide"
                        fullWidth
                        value={formData.tags}
                        onChange={(e) => handleInputChange('tags', e.target.value)}
                        disabled={isUploading}
                    />

                    {/* Video File Upload */}
                    <Box>
                        <input
                            accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                            style={{ display: 'none' }}
                            id="video-file-input"
                            type="file"
                            onChange={handleVideoSelect}
                            disabled={isUploading}
                        />
                        <label htmlFor="video-file-input">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<UploadIcon />}
                                fullWidth
                                disabled={isUploading}
                            >
                                {videoFile ? 'Change Video File' : (editingTutorial ? 'Change Video File (Optional)' : 'Upload Video File *')}
                            </Button>
                        </label>
                        {videoFile && (
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <VideoIcon color="primary" />
                                <Typography variant="body2">
                                    {videoFile.name} ({(videoFile.size / 1024 / 1024).toFixed(2)} MB)
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Thumbnail Upload */}
                    <Box>
                        <input
                            accept="image/jpeg,image/png,image/webp"
                            style={{ display: 'none' }}
                            id="thumbnail-file-input"
                            type="file"
                            onChange={handleThumbnailSelect}
                            disabled={isUploading}
                        />
                        <label htmlFor="thumbnail-file-input">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<ImageIcon />}
                                fullWidth
                                disabled={isUploading}
                            >
                                {thumbnailFile ? 'Change Thumbnail' : 'Upload Thumbnail (Optional)'}
                            </Button>
                        </label>
                        {thumbnailFile && (
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ImageIcon color="primary" />
                                <Typography variant="body2">
                                    {thumbnailFile.name} ({(thumbnailFile.size / 1024 / 1024).toFixed(2)} MB)
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={isUploading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isUploading || !formData.title || (!formData.category_id && !newCategoryName) || (!videoFile && !editingTutorial)}
                    startIcon={isUploading ? <CircularProgress size={20} /> : <UploadIcon />}
                >
                    {isUploading ? (editingTutorial ? 'Updating...' : 'Uploading...') : (editingTutorial ? 'Update Tutorial' : 'Upload Tutorial')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
